// This file is duplicated in frontend and backend till a way to share ts code is found
// Any changes made here should be copied to both places

import sortBy from 'lodash/sortBy';
const RAFIKI_PENALTY = 10;
export const NOCARD = "NOCARD";

export enum PlayerStatus {
    Pending = 'Pending',
    Approved = 'Approved',
    Denied = 'Denied',
    Limbo = 'Limbo',
}

export const sum = (arr: Array<number>) => {
    return arr.reduce((x, acc) => {
        return x + acc;
    }, 0);
};

export interface Game {
    roomCode: string;
    rounds: number;
    playerCount: number;
    // People (not computers) at the table; recorded from Nov 2026. Absent on older games.
    humanCount?: number;
    scores: any;
    winners: string[];
}

// House rules. The game document stores one of these strings; missing means "rafiki", so
// older games are unaffected. Each preset is a small declarative config (below) and the
// engine, bots, scoring and wording all read from it. The three guide pages
// (/cambio-rules, /cabo-card-game-rules, /kaboo-card-game-rules) link to /create?rules=<preset>.
export type RuleSet = "rafiki" | "gringo" | "cambio" | "cabo" | "kaboo";
export const RULE_SETS: RuleSet[] = ["rafiki", "gringo", "cambio", "cabo", "kaboo"];
export const ruleSetOf = (rules?: string): RuleSet =>
    RULE_SETS.includes(rules as RuleSet) ? (rules as RuleSet) : "rafiki";

// Card "ranks" as the presets name them: "A", "2".."10", "J", "Q", plus "RK" (red King),
// "BK" (black King) and "Joker".
export type Rank = string;
export const cardRank = (card: string): Rank | undefined => {
    if (!card) return undefined;
    if (card === "XJ" || card === "YJ") return "Joker";
    const suit = card.slice(-1);
    const number = card.slice(0, -1);
    if (!["H", "D", "C", "S"].includes(suit)) return undefined;
    if (number === "K") return suit === "H" || suit === "D" ? "RK" : "BK";
    if (["A", "2", "3", "4", "5", "6", "7", "8", "9", "10", "J", "Q"].includes(number)) return number;
    return undefined;
};

export type SlamRule = "any" | "own" | "none";

export interface RulePreset {
    id: RuleSet;
    name: string; // "Cambio"
    callWord: string; // what the caller shouts: "CAMBIO"
    values: { redKing: number; blackKing: number; joker: number; jack: number; queen: number }; // Ace 1, 2-10 face value
    peek: Rank[]; // discard to look at one of your own cards
    spy: Rank[]; // discard to look at an opponent's card
    blindSwap: Rank[]; // discard to swap one of yours with an opponent's, unseen
    lookAndSwap: Rank[]; // discard to look at an opponent's card, then swap
    discardAnyCard: boolean; // a drawn card may go straight on the discard pile instead of into your hand
    slam: SlamRule; // slam a matching card from any hand, your own hand only, or not at all
    slamPenaltyCards: number; // cards drawn into your hand for a wrong slam
    callerPenalty: number; // added to the caller's score when they're not the lowest
    callerBonus: number; // taken off the caller's score when they are the lowest
    callerWinsTies: boolean; // a caller who ties for lowest still wins (otherwise a tie is a failed call)
    lowestScoresZero: boolean; // the winner(s) of the round score 0 rather than their hand
}

const RAFIKI: RulePreset = {
    id: "rafiki",
    name: "Rafiki",
    callWord: "RAFIKI",
    values: { redKing: -1, blackKing: 10, joker: -1, jack: 10, queen: 10 },
    peek: ["7", "8"],
    spy: ["9", "10"],
    blindSwap: ["J", "Q", "BK"],
    lookAndSwap: [],
    discardAnyCard: false,
    slam: "any",
    slamPenaltyCards: 1,
    callerPenalty: RAFIKI_PENALTY,
    callerBonus: 0,
    callerWinsTies: false,
    lowestScoresZero: false,
};

export const RULE_PRESETS: { [id in RuleSet]: RulePreset } = {
    rafiki: RAFIKI,
    // Gringo (rules as Sam learned them, /gringo-card-game-rules)
    gringo: {
        ...RAFIKI,
        id: "gringo",
        name: "Gringo",
        callWord: "GRINGO",
        values: { ...RAFIKI.values, redKing: -3, joker: 0 },
        peek: ["10"],
        spy: ["J"],
        blindSwap: ["Q"],
        lookAndSwap: ["BK"],
        discardAnyCard: true,
    },
    // Cambio (/cambio-rules, as cambiocardgame.com and most rules sites describe it): 7-8 peek,
    // 9-10 spy, Jack/Queen blind swap, black King look and swap; any drawn card can be thrown;
    // stick a matching card from any hand (give the owner one of yours), one penalty card if wrong;
    // no points penalty for a failed call, but the caller loses a tie; red King -1, Joker 0.
    cambio: {
        ...RAFIKI,
        id: "cambio",
        name: "Cambio",
        callWord: "CAMBIO",
        values: { ...RAFIKI.values, joker: 0 },
        blindSwap: ["J", "Q"],
        lookAndSwap: ["BK"],
        discardAnyCard: true,
        slam: "any",
        callerPenalty: 0,
    },
    // Cabo (/cabo-card-game-rules, the published game on a normal deck): 7-8 peek, 9-10 spy,
    // Jack/Queen (the 11 and 12) blind swap, no other powers; any drawn card can be discarded; no
    // slamming (the boxed game has matching on your turn instead); the lowest hand scores 0, a
    // caller who isn't lowest adds 5, a caller who ties for lowest wins. Cards score their number:
    // Joker 0, Ace 1, 2-10, Jack 11, Queen 12, King 13 (the boxed deck runs 0 to 13).
    cabo: {
        ...RAFIKI,
        id: "cabo",
        name: "Cabo",
        callWord: "CABO",
        values: { redKing: 13, blackKing: 13, joker: 0, jack: 11, queen: 12 },
        blindSwap: ["J", "Q"],
        lookAndSwap: [],
        discardAnyCard: true,
        slam: "none",
        slamPenaltyCards: 0,
        callerPenalty: 5,
        callerWinsTies: true,
        lowestScoresZero: true,
    },
    // Kaboo (/kaboo-card-game-rules, UK rules): same powers as Cambio, any drawn card can be
    // discarded, snap from any hand with two penalty cards for a wrong snap, caller -5 if lowest
    // and +5 if not (a tie is not a win); Joker -1, red King 0, Jack 11, Queen 12, black King 13.
    kaboo: {
        ...RAFIKI,
        id: "kaboo",
        name: "Kaboo",
        callWord: "KABOO",
        values: { redKing: 0, blackKing: 13, joker: -1, jack: 11, queen: 12 },
        blindSwap: ["J", "Q"],
        lookAndSwap: ["BK"],
        discardAnyCard: true,
        slam: "any",
        slamPenaltyCards: 2,
        callerPenalty: 5,
        callerBonus: 5,
    },
};

export const presetOf = (rules?: string): RulePreset => RULE_PRESETS[ruleSetOf(rules)];

export const cardScore = (card: string, rules?: string): number => {
    if (card === NOCARD) {
        return 0;
    }
    const { values } = presetOf(rules);
    const rank = cardRank(card);
    switch (rank) {
        case "Joker":
            return values.joker;
        case "RK":
            return values.redKing;
        case "BK":
            return values.blackKing;
        case "J":
            return values.jack;
        case "Q":
            return values.queen;
        case "A":
            return 1;
        default:
            return Number(card.slice(0, -1));
    }
};

export const handScore = (hand: Array<string>, rules?: string): number => {
    if (!hand) {
        return 0;
    }
    const values = hand.map((card) => cardScore(card, rules));
    return sum(values);
};

export interface RoundScore {
    id: string;
    roundScore: number;
    penaltyAdded: boolean; // the caller wasn't lowest and paid the preset's penalty
    bonusAdded: boolean; // the caller was lowest and got the preset's bonus
}

// Scores one finished round under its house rules: each hand's points, then the caller's
// penalty or bonus (and, in Cabo, the winner's hand scoring 0). Sorted lowest first.
export const scoreRound = (hands: { [playerId: string]: string[] }, playerRafiki: string, rules?: string): RoundScore[] => {
    const preset = presetOf(rules);
    const playerIds = Object.keys(hands || {});
    const handScores: { [id: string]: number } = {};
    playerIds.forEach((id) => {
        handScores[id] = handScore(hands[id], rules);
    });
    const minScore = Math.min(...playerIds.map((id) => handScores[id]));
    const lowest = playerIds.filter((id) => handScores[id] === minScore);
    const caller = playerRafiki && playerIds.includes(playerRafiki) ? playerRafiki : "";
    const callerLowest = Boolean(caller) && lowest.includes(caller) && (preset.callerWinsTies || lowest.length === 1);
    const winners = callerLowest ? [caller] : lowest;

    const scoreboard = playerIds.map((id): RoundScore => {
        let roundScore = handScores[id];
        if (preset.lowestScoresZero && winners.includes(id)) {
            roundScore = 0;
        }
        let penaltyAdded = false;
        let bonusAdded = false;
        if (id === caller) {
            if (callerLowest) {
                roundScore -= preset.callerBonus;
                bonusAdded = preset.callerBonus > 0;
            } else {
                roundScore += preset.callerPenalty;
                penaltyAdded = preset.callerPenalty > 0; // Cambio: a failed call costs nothing but the tie
            }
        }
        return { id, roundScore, penaltyAdded, bonusAdded };
    });
    return sortBy(scoreboard, "roundScore");
};

export const getScores = (round: any) => {
    return scoreRound(round?.hands, round?.playerRafiki, round?.rules).map(({ id, roundScore }) => ({ id, roundScore }));
};

export const sumRoundScores = (roundScores: any[]): any[] => {
    const scoreMap: any = {};
    roundScores.forEach((scores: any) => {
        for (const player of scores) {
            if (!(player.id in scoreMap)) {
                scoreMap[player.id] = {
                    id: player.id,
                    score: 0,
                };
            }
            scoreMap[player.id].score += player.roundScore;
        }
    });

    const result = Object.keys(scoreMap).map((playerId) => {
        return {
            ...scoreMap[playerId],
        };
    });
    return result;
};

export const getTotalPlayerCount = (gameState: any) => {
    return gameState?.playerIds.length;
};

export const getPlayerIdsForScoring = (gameState: any) => {
    return [...(gameState?.allPlayerIds || []).filter((playerId: string) => {
        return gameState?.playerStatus?.[playerId] === PlayerStatus.Approved && !(playerId in (gameState?.bots || {}));
    }), ...(gameState?.leftPlayerIds || [])]
};

export const squareAbsValue = (x: number) => {
    if (x < 0) {
        return -1 * Math.pow(x, 2);
    }
    return Math.pow(x, 2);
};

// Weights from https://docs.google.com/spreadsheets/d/1CrqNJQ6DKlrLwDDIHVUHpCtGB0rZBzwbd19_j_umdsY/edit?usp=sharing
const COUNT_WEIGHTS: { [key: number]: number } = {
    2: 1,
    3: Math.sqrt(1.1),
    4: Math.sqrt(1.2),
    5: Math.sqrt(1.3),
    6: Math.sqrt(1.4),
};
const ROUND_WEIGHTS: { [key: number]: number } = {
    1: Math.sqrt(0.25),
    3: Math.sqrt(0.5),
    5: 1,
    10: Math.sqrt(1.25),
};
// A game played only against computers counts half as much as the same game against people.
export const COMPUTER_GAME_WEIGHT = 0.5;

const nearestWeight = (weights: { [key: number]: number }, value: number) => {
    const keys = Object.keys(weights).map(Number);
    if (!Number.isFinite(value)) return weights[keys[0]];
    const nearest = keys.reduce((best, k) => (Math.abs(k - value) < Math.abs(best - value) ? k : best), keys[0]);
    return weights[nearest];
};

// Every game is weighted, including games whose table size or round count isn't in the tables
// above (they take the nearest value) and games against computers (which used to skip the
// weighting entirely when their numbers weren't in the tables).
export const getGameWeight = (game: Partial<Game>) => {
    const count = Math.min(6, Math.max(2, Number(game?.playerCount) || 2));
    let weight = nearestWeight(COUNT_WEIGHTS, count) * nearestWeight(ROUND_WEIGHTS, Number(game?.rounds));
    if (game?.humanCount === 1) {
        weight = weight * COMPUTER_GAME_WEIGHT;
    }
    return weight;
};

export const getLeaderBoardEntry = (gameDocs: Game[], playerId: string, playerName: string) => {
    const plays = gameDocs.length;
    const numerator = squareAbsValue(
        sum(
            gameDocs.map((game) => {
                const won = game?.winners?.length === 1 && game?.winners?.includes(playerId);
                return (won ? 1 : -0.05) * getGameWeight(game);
            })
        )
    );
    const diff = plays === 0 ? 0 : numerator / Math.pow(plays, 1.1);
    const scale = 90;
    const score = 1500 + Math.round(scale * diff);

    const wins = gameDocs.filter((game: Game) => game?.winners?.includes(playerId) && game?.winners?.length === 1).length;
    return {
        wins,
        plays,
        score,
        name: playerName,
    };
};

